import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@apollo/client/react';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { graphql } from '../../gql';
import type { SearchMaintenanceTasksQuery } from '../../gql/graphql';
import { DataGrid } from '../../components/data-grid';
import { Checkbox, PageHeader, SelectInput, TextInput, displayInteger, displayText } from '../../components/ui';

const MAINTENANCE_SEARCH_FILTER_OPTIONS_QUERY = graphql(`
  query MaintenanceTaskSearchFilterOptions($areaId: Int, $countryId: Int) {
    maintenanceTaskSearchFilterOptions(areaId: $areaId, countryId: $countryId) {
      areas { id name }
      countries { id name }
      businessLines { id name }
    }
  }
`);

const SEARCH_MAINTENANCE_TASKS_QUERY = graphql(`
  query SearchMaintenanceTasks($filter: MaintenanceTaskSearchFilterInput) {
    searchMaintenanceTasks(filter: $filter) {
      id plantId plantName assetId assetCode brand model latestSmr
      nextServiceDueHours assetHours serviceType completed completedBy
    }
  }
`);

type MaintenanceRow = SearchMaintenanceTasksQuery['searchMaintenanceTasks'][number];

function id(value: string | null): number | null {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

const columns: ColDef<MaintenanceRow>[] = [
  {
    field: 'plantName', headerName: 'Plant Name', minWidth: 200,
    cellRenderer: ({ data }: ICellRendererParams<MaintenanceRow>) => data && (
      <Link className="text-primary hover:underline" to={`/plants/${data.plantId}`}>{displayText(data.plantName)}</Link>
    ),
  },
  {
    field: 'assetCode', headerName: 'Asset Code', minWidth: 150,
    cellRenderer: ({ data }: ICellRendererParams<MaintenanceRow>) => data && (
      <Link className="text-primary hover:underline" to={`/assets/${data.assetId}/maintenance`}>{displayText(data.assetCode)}</Link>
    ),
  },
  { field: 'brand', headerName: 'Brand' },
  { field: 'model', headerName: 'Model', minWidth: 150 },
  { field: 'latestSmr', headerName: 'Latest SMR', valueFormatter: ({ value }) => displayInteger(value) },
  {
    field: 'nextServiceDueHours', headerName: 'Next Service Due in Hours', minWidth: 220,
    sort: 'asc', sortIndex: 0,
    valueFormatter: ({ value }) => displayInteger(value),
    comparator: (first: number | null, second: number | null, _firstNode, _secondNode, descending) => {
      if (first == null) return second == null ? 0 : descending ? -1 : 1;
      if (second == null) return descending ? 1 : -1;
      return first - second;
    },
  },
  { field: 'assetHours', headerName: 'Asset Hours', valueFormatter: ({ value }) => displayInteger(value) },
  { field: 'serviceType', headerName: 'Service Type', minWidth: 150 },
  { field: 'completed', headerName: 'Completed', cellDataType: false, valueFormatter: ({ value }) => value == null ? '-' : value ? 'Yes' : 'No' },
  { field: 'completedBy', headerName: 'Completed By', minWidth: 160 },
  {
    colId: 'maintenance', headerName: 'Asset Maintenance', minWidth: 180, sortable: false, filter: false,
    cellRenderer: ({ data }: ICellRendererParams<MaintenanceRow>) => data && (
      <Link className="inline-flex items-center text-primary" to={`/assets/${data.assetId}/maintenance`}
        aria-label={`Open maintenance for ${displayText(data.assetCode)}`} title="Asset Maintenance">
        <OpenInNewIcon fontSize="small" />
      </Link>
    ),
  },
];

export function MaintenanceSearchPage() {
  const [params, setParams] = useSearchParams();
  const [defaultsEnabled, setDefaultsEnabled] = useState(true);
  return <MaintenanceSearch key={params.toString()} params={params} apply={setParams}
    defaultsEnabled={defaultsEnabled} disableDefaults={() => setDefaultsEnabled(false)} />;
}

function MaintenanceSearch({ params, apply, defaultsEnabled, disableDefaults }: {
  params: URLSearchParams;
  apply: (params: URLSearchParams) => void;
  defaultsEnabled: boolean;
  disableDefaults: () => void;
}) {
  const [draft, setDraft] = useState(() => ({
    area: params.get('area') ?? '', country: params.get('country') ?? '',
    bl: params.get('bl') ?? '', q: params.get('q') ?? '',
    completed: params.get('completed') === 'true',
  }));
  const applied = ['search', 'area', 'country', 'bl', 'q', 'completed'].some((key) => params.has(key));
  const [defaults, setDefaults] = useState({ area: defaultsEnabled && !applied && !params.has('area'), country: defaultsEnabled && !applied && !params.has('country') });
  const optionsQuery = useQuery(MAINTENANCE_SEARCH_FILTER_OPTIONS_QUERY, {
    variables: { areaId: id(draft.area), countryId: id(draft.country) },
  });
  const options = optionsQuery.error ? undefined : optionsQuery.data?.maintenanceTaskSearchFilterOptions;

  useEffect(() => {
    if (optionsQuery.loading || !options) return;
    if (defaults.area && !draft.area && options.areas.length === 1) {
      setDraft((previous) => ({ ...previous, area: String(options.areas[0].id) }));
    } else if (defaults.country && !draft.country && options.countries.length === 1) {
      setDraft((previous) => ({ ...previous, country: String(options.countries[0].id) }));
    }
  }, [optionsQuery.loading, options, defaults, draft.area, draft.country]);

  const resultsQuery = useQuery(SEARCH_MAINTENANCE_TASKS_QUERY, {
    variables: { filter: {
      areaId: id(params.get('area')), countryId: id(params.get('country')),
      businessLineId: id(params.get('bl')), searchTerm: params.get('q')?.trim() || null,
      includeCompleted: params.get('completed') === 'true',
    } },
    skip: !applied,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const rows = !applied || resultsQuery.loading || resultsQuery.error ? [] : [...(resultsQuery.data?.searchMaintenanceTasks ?? [])].sort(
    (first, second) => {
      if (first.nextServiceDueHours == null && second.nextServiceDueHours != null) return 1;
      if (second.nextServiceDueHours == null && first.nextServiceDueHours != null) return -1;
      return (first.nextServiceDueHours ?? 0) - (second.nextServiceDueHours ?? 0)
        || (first.assetCode ?? '').localeCompare(second.assetCode ?? '') || first.id - second.id;
    },
  );

  function find() {
    const next = new URLSearchParams({ search: '1' });
    for (const field of ['area', 'country', 'bl', 'q'] as const) {
      if (draft[field].trim()) next.set(field, draft[field].trim());
    }
    if (draft.completed) next.set('completed', 'true');
    if (applied && next.toString() === params.toString()) {
      void resultsQuery.refetch().catch(() => {});
      return;
    }
    apply(next);
  }

  function clear() {
    setDraft({ area: '', country: '', bl: '', q: '', completed: false });
    setDefaults({ area: false, country: false });
    disableDefaults();
    apply(new URLSearchParams());
  }

  return (
    <main className="max-w-[1440px] mx-auto px-6 py-8 min-w-0">
      <PageHeader title="Maintenance Task Search" />
      <form className="flex flex-wrap items-center gap-3 mb-4" onSubmit={(event) => { event.preventDefault(); find(); }}>
        <TextInput search label="Search" sx={{ width: 320, maxWidth: '100%' }} value={draft.q} placeholder="Asset code, serial number or model"
          onChange={(event) => setDraft({ ...draft, q: event.target.value })} />
        <SelectInput label="Area" fullWidth={false} sx={{ minWidth: 160, maxWidth: '100%' }} value={draft.area}
          disabled={optionsQuery.loading || !!optionsQuery.error}
          options={[{ value: '', label: 'All Areas' }, ...(options?.areas ?? []).map((option) => ({ value: option.id, label: option.name }))]}
          onChange={(event) => {
            setDraft({ ...draft, area: String(event.target.value), country: '', bl: '' });
            setDefaults({ area: false, country: true });
          }} />
        <SelectInput label="Country" fullWidth={false} sx={{ minWidth: 160, maxWidth: '100%' }} value={draft.country}
          disabled={optionsQuery.loading || !!optionsQuery.error}
          options={[{ value: '', label: 'All Countries' }, ...(options?.countries ?? []).map((option) => ({ value: option.id, label: option.name }))]}
          onChange={(event) => {
            setDraft({ ...draft, country: String(event.target.value), bl: '' });
            setDefaults({ ...defaults, country: false });
          }} />
        <SelectInput label="Business Line" fullWidth={false} sx={{ minWidth: 160, maxWidth: '100%' }} value={draft.bl}
          disabled={optionsQuery.loading || !!optionsQuery.error}
          options={[{ value: '', label: 'All Business Lines' }, ...(options?.businessLines ?? []).map((option) => ({ value: option.id, label: option.name }))]}
          onChange={(event) => setDraft({ ...draft, bl: String(event.target.value) })} />
        <Checkbox label="Include completed" checked={draft.completed}
          onChange={(checked) => setDraft({ ...draft, completed: checked })} />
        <Button type="submit" variant="contained" startIcon={<SearchIcon />}>Find</Button>
        <Button variant="outlined" startIcon={<ClearIcon />} onClick={clear}>Clear</Button>
      </form>
      {optionsQuery.loading && <p role="status" className="mb-4 text-foreground-faded">Loading filter options...</p>}
      {!optionsQuery.loading && options && options.areas.length === 0 && options.countries.length === 0 && options.businessLines.length === 0
        && <p role="status" className="mb-4 text-foreground-faded">No filter options available.</p>}
      {optionsQuery.error && (
        <div role="alert" className="mb-4 text-negative">
          <p>Unable to load filter options: {optionsQuery.error.message}</p>
          <Button startIcon={<RefreshIcon />} onClick={() => void optionsQuery.refetch().catch(() => {})}>Retry filter options</Button>
        </div>
      )}
      {!applied ? <p role="status" className="py-12 text-center text-foreground-faded">No search applied.</p>
        : resultsQuery.loading ? <p role="status" className="py-12 text-center text-foreground-faded">Loading maintenance tasks...</p>
          : resultsQuery.error ? (
            <div role="alert" className="text-negative">
              <p>Unable to load maintenance tasks: {resultsQuery.error.message}</p>
              <Button startIcon={<RefreshIcon />} onClick={() => void resultsQuery.refetch().catch(() => {})}>Retry results</Button>
            </div>
          ) : rows.length === 0 ? <p role="status" className="py-12 text-center text-foreground-faded">No maintenance tasks match the current filters.</p>
            : <section aria-label="Maintenance task results" className="min-w-0">
              <DataGrid rowData={rows} columnDefs={columns} getRowId={({ data }) => String(data.id)}
                pagination paginationPageSize={20} paginationPageSizeSelector={[10, 20, 30, 40]} height="65vh" />
            </section>}
    </main>
  );
}